import { useState, useCallback } from "react";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import Radio from "@mui/material/Radio";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useOpportunityCategories } from "@/hooks/queries";
import { useNotification } from "@/hooks/useNotification";
import type { OpportunityCategory, OpportunitySubcategory, ColumnDefinition } from "@api/opportunity";
import {
  apiCreateOpportunityCategory,
  apiUpdateOpportunityCategory,
  apiDeleteOpportunityCategory,
  apiGetCategoryUsage,
  apiUpdateCategoryColumns,
  apiCreateSubcategory,
  apiUpdateSubcategory,
  apiDeleteSubcategory,
} from "@api/opportunity";
import styles from "./styles.module.css";

const headerToFieldKey = (header: string): string =>
  header
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");

export const CategoriesSection = () => {
  const { data: categories = [] } = useOpportunityCategories();
  const queryClient = useQueryClient();
  const { notifySuccess, notifyError } = useNotification();
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [addingCategory, setAddingCategory] = useState(false);
  const [newCatName, setNewCatName] = useState("");

  const selectedCategory = categories.find((c) => c.id === selectedCategoryId) ?? null;
  const sortedCategories = [...categories].sort((a, b) => a.sort_order - b.sort_order);

  const createCatMutation = useMutation({
    mutationFn: () => apiCreateOpportunityCategory(newCatName, categories.length),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["opportunityCategories"] });
      notifySuccess("Category created");
      setAddingCategory(false);
      setNewCatName("");
    },
    onError: () => notifyError("Failed to create category"),
  });

  const reorderMutation = useMutation({
    mutationFn: async ({ catA, catB }: { catA: OpportunityCategory; catB: OpportunityCategory }) => {
      await Promise.all([
        apiUpdateOpportunityCategory(catA.id, catA.name, catB.sort_order),
        apiUpdateOpportunityCategory(catB.id, catB.name, catA.sort_order),
      ]);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["opportunityCategories"] });
    },
    onError: () => notifyError("Failed to reorder categories"),
  });

  const moveCategory = (index: number, direction: "up" | "down") => {
    const swapIndex = direction === "up" ? index - 1 : index + 1;
    const catA = sortedCategories[index];
    const catB = sortedCategories[swapIndex];
    if (catA && catB) reorderMutation.mutate({ catA, catB });
  };

  return (
    <div className={styles.section}>
      <h2 className={styles.sectionTitle}>Opportunity Categories</h2>
      <div className={styles.layout}>
        <div className={styles.nav}>
          <List dense>
            {sortedCategories.map((cat, index) => (
              <ListItemButton
                key={cat.id}
                selected={selectedCategoryId === cat.id}
                onClick={() => setSelectedCategoryId(cat.id)}
                sx={{ pr: 1 }}
              >
                <ListItemText primary={cat.name} />
                <div style={{ display: "flex", marginLeft: 4 }} onClick={(e) => e.stopPropagation()}>
                  <IconButton
                    size="small"
                    disabled={index === 0 || reorderMutation.isPending}
                    onClick={() => moveCategory(index, "up")}
                    aria-label={`Move ${cat.name} up`}
                    sx={{ p: 0.25 }}
                  >
                    <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    disabled={index === sortedCategories.length - 1 || reorderMutation.isPending}
                    onClick={() => moveCategory(index, "down")}
                    aria-label={`Move ${cat.name} down`}
                    sx={{ p: 0.25 }}
                  >
                    <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </div>
              </ListItemButton>
            ))}
          </List>
          {addingCategory ? (
            <div style={{ display: "flex", gap: 4, padding: "0 8px" }}>
              <TextField
                size="small"
                value={newCatName}
                onChange={(e) => setNewCatName(e.target.value)}
                placeholder="Category name"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter" && newCatName.trim()) createCatMutation.mutate();
                  if (e.key === "Escape") setAddingCategory(false);
                }}
              />
              <IconButton size="small" onClick={() => createCatMutation.mutate()} disabled={!newCatName.trim()}>
                <CheckIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => setAddingCategory(false)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </div>
          ) : (
            <Button size="small" startIcon={<AddIcon />} onClick={() => setAddingCategory(true)} sx={{ ml: 1 }}>
              Add Category
            </Button>
          )}
        </div>
        <div className={styles.content}>
          {selectedCategory ? (
            <CategoryDetail category={selectedCategory} />
          ) : (
            <Typography color="text.secondary">Select a category to edit its columns and subcategories</Typography>
          )}
        </div>
      </div>
    </div>
  );
};

const CategoryDetail = ({ category }: { category: OpportunityCategory }) => {
  const queryClient = useQueryClient();
  const { notifySuccess, notifyError } = useNotification();
  const { data: categories = [] } = useOpportunityCategories();
  const [editingName, setEditingName] = useState(false);
  const [name, setName] = useState(category.name);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reassignTarget, setReassignTarget] = useState("");
  const [usageData, setUsageData] = useState<{ opportunity_count: number } | null>(null);

  const updateCatMutation = useMutation({
    mutationFn: () => apiUpdateOpportunityCategory(category.id, name, category.sort_order),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["opportunityCategories"] });
      notifySuccess("Category updated");
      setEditingName(false);
    },
    onError: () => notifyError("Failed to update category"),
  });

  const deleteCatMutation = useMutation({
    mutationFn: () => apiDeleteOpportunityCategory(category.id, reassignTarget || undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["opportunityCategories"] });
      notifySuccess("Category deleted");
      setDeleteDialogOpen(false);
    },
    onError: () => notifyError("Failed to delete category"),
  });

  const handleDeleteClick = async () => {
    try {
      const usage = await apiGetCategoryUsage(category.id);
      setUsageData(usage);
      if (usage.opportunity_count > 0) {
        setDeleteDialogOpen(true);
      } else {
        if (window.confirm(`Delete "${category.name}"?`)) {
          deleteCatMutation.mutate();
        }
      }
    } catch {
      notifyError("Failed to check category usage");
    }
  };

  const otherCategories = categories.filter((c) => c.id !== category.id);

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
        {editingName ? (
          <>
            <TextField
              size="small"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") updateCatMutation.mutate();
                if (e.key === "Escape") { setEditingName(false); setName(category.name); }
              }}
            />
            <IconButton size="small" onClick={() => updateCatMutation.mutate()}>
              <CheckIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => { setEditingName(false); setName(category.name); }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        ) : (
          <>
            <Typography variant="h6">{category.name}</Typography>
            <IconButton size="small" onClick={() => { setName(category.name); setEditingName(true); }}>
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" color="error" onClick={handleDeleteClick}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </>
        )}
      </div>

      <SubcategoryEditor
        categoryId={category.id}
        subcategories={category.subcategories}
        allCategories={categories}
      />

      <Divider sx={{ my: 3 }} />

      <ColumnEditor categoryId={category.id} columns={category.columns} />

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Category</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            This category has {usageData?.opportunity_count ?? 0} opportunities. Select where to move them:
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel>Move to</InputLabel>
            <Select value={reassignTarget} onChange={(e) => setReassignTarget(e.target.value)} label="Move to">
              {otherCategories.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => deleteCatMutation.mutate()}
            disabled={!reassignTarget || deleteCatMutation.isPending}
          >
            Delete & Move
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

const ColumnEditor = ({ categoryId, columns: initialColumns }: {
  categoryId: string;
  columns: OpportunityCategory["columns"];
}) => {
  const queryClient = useQueryClient();
  const { notifySuccess, notifyError } = useNotification();
  const [columns, setColumns] = useState<ColumnDefinition[]>(
    [...initialColumns]
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((c) => ({
        field_key: c.field_key,
        header: c.header,
        field_type: c.field_type,
        is_primary_sort_field: c.is_primary_sort_field,
        sort_order: c.sort_order,
        open_edit: c.open_edit,
      }))
  );
  const [deleteConfirm, setDeleteConfirm] = useState<{ index: number; header: string } | null>(null);
  const [typeConfirm, setTypeConfirm] = useState<{ index: number; newType: string } | null>(null);

  const saveMutation = useMutation({
    mutationFn: () => apiUpdateCategoryColumns(categoryId, columns.map((c, i) => ({ ...c, sort_order: i }))),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["opportunityCategories"] });
      notifySuccess("Columns saved");
    },
    onError: () => notifyError("Failed to save columns"),
  });

  const updateColumn = (index: number, field: keyof ColumnDefinition, value: string | boolean) => {
    setColumns((prev) => {
      const next = [...prev];
      if (field === "is_primary_sort_field" && value === true) {
        next.forEach((c) => (c.is_primary_sort_field = false));
      }
      const updated = { ...next[index], [field]: value };
      if (field === "header" && typeof value === "string" && !next[index].field_key) {
        updated.field_key = headerToFieldKey(value);
      }
      next[index] = updated;
      return next;
    });
  };

  const handleTypeChange = (index: number, newType: string) => {
    const col = columns[index];
    if (col.field_key && col.field_type !== newType) {
      setTypeConfirm({ index, newType });
    } else {
      updateColumn(index, "field_type", newType);
    }
  };

  const addColumn = () => {
    setColumns((prev) => [
      ...prev,
      { field_key: "", header: "", field_type: "text", is_primary_sort_field: false, sort_order: prev.length, open_edit: false },
    ]);
  };

  const handleRemoveClick = (index: number) => {
    const col = columns[index];
    if (col.field_key) {
      setDeleteConfirm({ index, header: col.header });
    } else {
      setColumns((prev) => prev.filter((_, i) => i !== index));
    }
  };

  return (
    <div>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Columns</Typography>
      <div className={styles.columnList}>
        {columns.map((col, i) => (
          <div key={i} className={styles.columnRow}>
            <TextField
              size="small"
              value={col.header}
              onChange={(e) => updateColumn(i, "header", e.target.value)}
              placeholder="Column name"
              className={styles.headerInput}
            />
            <TextField
              select
              size="small"
              value={col.field_type}
              onChange={(e) => handleTypeChange(i, e.target.value)}
              className={styles.typeSelect}
            >
              <MenuItem value="text">Text</MenuItem>
              <MenuItem value="date">Date</MenuItem>
            </TextField>
            <Radio
              checked={col.is_primary_sort_field}
              onChange={() => updateColumn(i, "is_primary_sort_field", true)}
              size="small"
              title="Primary sort"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={col.open_edit}
                  onChange={(e) => updateColumn(i, "open_edit", e.target.checked)}
                  size="small"
                />
              }
              label="Open Edit"
              title="When checked, all users (not just admins) can edit this column's value on individual opportunities"
              sx={{ mr: 0, whiteSpace: "nowrap" }}
            />
            <IconButton size="small" onClick={() => handleRemoveClick(i)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
        <Button size="small" startIcon={<AddIcon />} onClick={addColumn}>
          Add Column
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending}
        >
          Save Columns
        </Button>
      </div>

      <Dialog open={deleteConfirm !== null} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Delete Column</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the "{deleteConfirm?.header}" column? Existing data in this column will no longer be visible.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            If you need to recover the data, re-add a column with the same name.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              setColumns((prev) => prev.filter((_, i) => i !== deleteConfirm!.index));
              setDeleteConfirm(null);
            }}
          >
            Delete Column
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={typeConfirm !== null} onClose={() => setTypeConfirm(null)}>
        <DialogTitle>Change Column Type</DialogTitle>
        <DialogContent>
          <Typography>
            Changing the column type will not convert existing data. Values may display incorrectly.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTypeConfirm(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              updateColumn(typeConfirm!.index, "field_type", typeConfirm!.newType);
              setTypeConfirm(null);
            }}
          >
            Change Type
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

const SubcategoryEditor = ({ categoryId, subcategories, allCategories }: {
  categoryId: string;
  subcategories: OpportunitySubcategory[];
  allCategories: OpportunityCategory[];
}) => {
  const queryClient = useQueryClient();
  const { notifySuccess, notifyError } = useNotification();
  const [addingName, setAddingName] = useState("");
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [reassignTarget, setReassignTarget] = useState("");

  const sorted = [...subcategories].sort((a, b) => a.sort_order - b.sort_order);

  const createMutation = useMutation({
    mutationFn: () => apiCreateSubcategory(addingName, categoryId, subcategories.length),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["opportunityCategories"] });
      notifySuccess("Subcategory created");
      setAdding(false);
      setAddingName("");
    },
    onError: () => notifyError("Failed to create subcategory"),
  });

  const updateMutation = useMutation({
    mutationFn: (id: string) => {
      const sub = subcategories.find((s) => s.id === id);
      return apiUpdateSubcategory(id, editName, sub?.sort_order ?? 0);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["opportunityCategories"] });
      notifySuccess("Subcategory updated");
      setEditingId(null);
    },
    onError: () => notifyError("Failed to update subcategory"),
  });

  const deleteMutation = useMutation({
    mutationFn: () => apiDeleteSubcategory(deletingId!, reassignTarget || undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["opportunityCategories"] });
      notifySuccess("Subcategory deleted");
      setDeleteDialogOpen(false);
      setDeletingId(null);
      setReassignTarget("");
    },
    onError: () => notifyError("Failed to delete subcategory"),
  });

  const handleDelete = useCallback(async (id: string) => {
    setDeletingId(id);
    try {
      await apiDeleteSubcategory(id);
      queryClient.invalidateQueries({ queryKey: ["opportunityCategories"] });
      notifySuccess("Subcategory deleted");
    } catch (err: any) {
      if (err?.response?.status === 409) {
        setDeleteDialogOpen(true);
      } else {
        notifyError("Failed to delete subcategory");
      }
    }
  }, [queryClient, notifySuccess, notifyError]);

  const otherSubcategories = subcategories.filter((s) => s.id !== deletingId);

  return (
    <div>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Subcategories</Typography>
      {sorted.length === 0 && !adding && (
        <Typography color="text.secondary" variant="body2">No subcategories for this category</Typography>
      )}
      <div className={styles.subcategoryList}>
        {sorted.map((sub) => (
          <div key={sub.id} className={styles.subcategoryItem}>
            {editingId === sub.id ? (
              <>
                <TextField
                  size="small"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter") updateMutation.mutate(sub.id);
                    if (e.key === "Escape") setEditingId(null);
                  }}
                />
                <IconButton size="small" onClick={() => updateMutation.mutate(sub.id)}>
                  <CheckIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => setEditingId(null)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </>
            ) : (
              <>
                <span className={styles.categoryName}>{sub.name}</span>
                <IconButton size="small" onClick={() => { setEditingId(sub.id); setEditName(sub.name); }}>
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" color="error" onClick={() => handleDelete(sub.id)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </>
            )}
          </div>
        ))}
      </div>
      {adding ? (
        <div style={{ display: "flex", gap: 4, marginTop: 8 }}>
          <TextField
            size="small"
            value={addingName}
            onChange={(e) => setAddingName(e.target.value)}
            placeholder="Subcategory name"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter" && addingName.trim()) createMutation.mutate();
              if (e.key === "Escape") setAdding(false);
            }}
          />
          <IconButton size="small" onClick={() => createMutation.mutate()} disabled={!addingName.trim()}>
            <CheckIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => setAdding(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      ) : (
        <Button size="small" startIcon={<AddIcon />} onClick={() => setAdding(true)} sx={{ mt: 1 }}>
          Add Subcategory
        </Button>
      )}

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Subcategory</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            This subcategory has opportunities. Select where to move them:
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel>Move to</InputLabel>
            <Select value={reassignTarget} onChange={(e) => setReassignTarget(e.target.value)} label="Move to">
              {otherSubcategories.map((s) => (
                <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => deleteMutation.mutate()}
            disabled={!reassignTarget || deleteMutation.isPending}
          >
            Delete & Move
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
